import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import type { MenuItem } from '@/types/menu.types';
import { getMenuLabel } from '@/utils/menu.utils';

const MENU_BUTTON_CLASS =
  'relative h-9 p-0 group-data-[collapsible=icon]:h-9! group-data-[collapsible=icon]:w-full! group-data-[collapsible=icon]:p-0! data-[active=true]:bg-primary/10 data-[active=true]:text-primary data-[active=true]:hover:bg-primary/10 data-[active=true]:hover:text-primary data-[active=true]:active:bg-primary/10 data-[active=true]:active:text-primary';

// Boîte d'icône de la largeur du logo (size-9) : en mode replié, les icônes restent
// centrées sous le logo ; la largeur intrinsèque des icônes FontAwesome varie.
// Pas de taille forcée sur l'icône : elle garde la taille FontAwesome par défaut.
const ICON_BOX_CLASS = 'flex size-9 shrink-0 items-center justify-center';

const LABEL_CLASS =
  'whitespace-nowrap transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:opacity-0';

const GROUP_CLASS = 'gap-0 px-3 py-0';

export const isPathActive = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

interface NavMainProps {
  items: MenuItem[];
  pathname: string;
}

interface NavSection {
  key: string;
  group?: MenuItem;
  items: MenuItem[];
}

/** Regroupe les pages consécutives de premier niveau ; chaque item à enfants forme sa propre section. */
const toSections = (items: MenuItem[]): NavSection[] =>
  items.reduce<NavSection[]>((sections, item) => {
    if (item.children?.length) {
      sections.push({ key: item.id, group: item, items: item.children });
      return sections;
    }
    const last = sections[sections.length - 1];
    if (last && !last.group) {
      last.items.push(item);
    } else {
      sections.push({ key: item.id, items: [item] });
    }
    return sections;
  }, []);

export const NavMain = ({ items, pathname }: NavMainProps) => {
  const { t } = useTranslation();
  const { closePeek } = useSidebar();

  return (
    <>
      {toSections(items).map((section) => (
        <SidebarGroup key={section.key} className={GROUP_CLASS}>
          {section.group ? (
            <SidebarGroupLabel data-testid={`nav-group-${section.group.id}`}>
              {getMenuLabel(section.group, t)}
            </SidebarGroupLabel>
          ) : null}
          <SidebarMenu>
            {section.items.map((item) =>
              item.path ? (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    asChild
                    isActive={isPathActive(pathname, item.path)}
                    className={MENU_BUTTON_CLASS}
                    onClick={closePeek}
                  >
                    <Link to={item.path} data-testid={`nav-item-${item.id}`}>
                      <span className={ICON_BOX_CLASS}>
                        {item.icon ? <FontAwesomeIcon icon={item.icon} /> : null}
                      </span>
                      <span className={LABEL_CLASS}>{getMenuLabel(item, t)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ) : null,
            )}
          </SidebarMenu>
        </SidebarGroup>
      ))}
    </>
  );
};
